import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useToast, usePostHog } from "@finitless/shared/hooks";
import {
  handleApiError,
  handleApiSuccess,
  stringToSlug,
  convertFileToBase64,
} from "@finitless/shared/lib";
import { Icon, Modal, Switch } from "../";
import {
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from "../../../hooks/useProducts";
import { DeleteConfirmationModal } from "../Modals";

const CreateProductModal = ({
  isOpen,
  onClose,
  businessId,
  categoryId,
  productToEdit = null,
  onToggleProduct,
  onProductDeleted,
  isLoadingToggle,
  hasPosIntegration,
}) => {
  const toast = useToast();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const isEditMode = !!productToEdit;
  const { trackActivation, trackEngagement } = usePostHog();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(productToEdit);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    image: null,
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when productToEdit changes
  useEffect(() => {
    if (productToEdit) {
      setCurrentProduct(productToEdit);
      setFormData({
        name: productToEdit.name || "",
        description: productToEdit.description || "",
        price: productToEdit.price?.toString() || "",
        image: productToEdit.images || null,
      });
    } else {
      setCurrentProduct(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        image: null,
      });
    }
    setErrors({});
  }, [productToEdit, isOpen]);

  const handleImageChange = async (file) => {
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 512 * 1024) {
        toast.error("Image size must be less than 512KB");
        return;
      }

      try {
        const base64String = await convertFileToBase64(file);
        setFormData((prev) => ({ ...prev, image: base64String }));
      } catch (error) {
        console.error("Error converting image to base64:", error);
        toast.error("Error processing image");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y punto decimal
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      // Limitar a 15 caracteres
      const limitedValue = value.slice(0, 15);
      setFormData((prev) => ({ ...prev, price: limitedValue }));
      if (errors.price) {
        setErrors((prev) => ({ ...prev, price: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.price?.trim()) {
      newErrors.price = "Price is required";
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = "Price must be a valid positive number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        name: formData.name?.trim(),
        description: formData.description?.trim() || "",
        price: formData.price?.trim(),
        slug: stringToSlug(formData.name || ""),
      };
      // Only include images if a new image was uploaded (base64)
      if (
        formData.image &&
        typeof formData.image === "string" &&
        formData.image.startsWith("data:image/")
      ) {
        payload.images = formData.image;
      }

      if (isEditMode) {
        // For update, don't include enabled field
        await updateMutation.mutateAsync({
          businessId,
          categoryId: currentProduct.category_id || categoryId,
          productId: currentProduct.id,
          ...payload,
        });

        // Track menu item update and price change
        if (
          payload.price &&
          currentProduct.price &&
          parseFloat(payload.price) !== parseFloat(currentProduct.price)
        ) {
          trackEngagement.priceChanged({
            business_id: businessId,
            product_id: currentProduct.id,
            old_price: currentProduct.price,
            new_price: payload.price,
          });
        }

        trackEngagement.menuItemUpdated({
          business_id: businessId,
          product_id: currentProduct.id,
          category_id: currentProduct.category_id || categoryId,
        });

        handleApiSuccess("Product updated successfully", toast);
      } else {
        // For create, include enabled field
        const createdProduct = await createMutation.mutateAsync({
          businessId,
          categoryId,
          payload: { ...payload, enabled: true },
        });

        // Track menu item creation (part of menu upload)
        trackActivation.menuUploaded({
          business_id: businessId,
          product_id: createdProduct?.id ?? payload.slug,
          category_id: categoryId,
        });

        handleApiSuccess("Product created successfully", toast);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: isEditMode
          ? "Failed to Update Product"
          : "Failed to Create Product",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleDelete = () => {
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentProduct || !categoryId) return;
    try {
      await deleteMutation.mutateAsync({
        businessId,
        categoryId: currentProduct.category_id || categoryId,
        productId: currentProduct.id,
      });
      handleApiSuccess("Product deleted successfully", toast);
      setIsDeleteOpen(false);
      if (onProductDeleted) {
        onProductDeleted(currentProduct.id);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Delete Product",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleToggleEnabled = async (value) => {
    if (!currentProduct || !onToggleProduct) return;
    try {
      await onToggleProduct(currentProduct.id, value);
      // Update local state after successful toggle
      setCurrentProduct({ ...currentProduct, enabled: value });
    } catch {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      image: null,
    });
    setErrors({});
    setIsDeleteOpen(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Product" : "Create Product"}
      subtitle={
        isEditMode
          ? "Update product details"
          : "Add a new product to this category"
      }
      maxWidth="max-w-md"
      className="ordering-catalog-surface"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-foreground mb-2 block text-sm font-medium">
            Image
          </label>
          <p className="text-muted-foreground mb-3 text-xs">
            Accepted formats: PNG, JPG, JPEG, WEBP (Max 512KB)
          </p>
          <div className="flex items-center justify-center gap-3">
            <div className="relative inline-block h-24 w-24">
              {formData.image ? (
                <>
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="border-border h-24 w-24 rounded-lg border-2 object-cover"
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                    <div className="flex items-center space-x-1">
                      <Icon name="Image" className="h-5 w-5 text-white" />
                      <Icon
                        name="MoreVertical"
                        className="h-5 w-5 text-white"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-border bg-card pointer-events-none flex h-24 w-24 items-center justify-center rounded-lg border">
                  <Icon
                    name="Image"
                    className="text-muted-foreground h-6 w-6"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e.target.files?.[0])}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                disabled={createMutation.isPending || updateMutation.isPending}
                aria-label="Upload image"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="text-foreground mb-1 block text-sm font-medium">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Product name"
            required
          />
          {errors.name && (
            <div className="text-destructive mt-1 text-xs">{errors.name}</div>
          )}
        </div>

        <div>
          <label className="text-foreground mb-1 block text-sm font-medium">
            Price <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handlePriceChange}
            maxLength={15}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="0"
            required
          />
          {errors.price && (
            <div className="text-destructive mt-1 text-xs">{errors.price}</div>
          )}
        </div>

        <div>
          <label className="text-foreground mb-1 block text-sm font-medium">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring text-foreground w-full resize-none rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Product description"
            rows={3}
          />
        </div>

        {/* Actions only shown in edit mode */}
        {isEditMode && !hasPosIntegration && (
          <>
            <div className="border-border border-t pt-4">
              <div className="space-y-4">
                <div className="border-border bg-card flex w-full items-center justify-between rounded-lg border px-4 py-2">
                  <span className="text-foreground text-sm font-medium">
                    {currentProduct?.enabled ? "Enabled" : "Disabled"} Product
                  </span>
                  <Switch
                    id="product-enabled"
                    checked={!!currentProduct?.enabled}
                    onCheckedChange={handleToggleEnabled}
                    disabled={
                      isLoadingToggle ||
                      createMutation.isPending ||
                      updateMutation.isPending
                    }
                  />
                </div>
                <Button
                  emphasis="solid"
                  intent="danger"
                  type="button"
                  onClick={handleDelete}
                  disabled={
                    deleteMutation.isPending ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon name="Trash" className="h-4 w-4" />
                  {deleteMutation.isPending ? "Deleting..." : "Delete Product"}
                </Button>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.name.trim() ||
              !formData.price.trim()
            }
            className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditMode
              ? updateMutation.isPending
                ? "Updating..."
                : "Update Product"
              : createMutation.isPending
                ? "Creating..."
                : "Create Product"}
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        itemName={currentProduct?.name}
        itemType="Product"
        isLoading={deleteMutation.isPending}
      />
    </Modal>
  );
};

export default CreateProductModal;
